import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useState } from 'react'
import { isNullOrBlank } from '../../../../helpers'
import { getBaseApiUrl, handleResponseData } from '../../../../services/api.service'
import { MutateRequest } from '../../../phase5/TransitOperationEditor/hooks/request-type'
import { NctsProfileRequest, NctsProfileResponse } from '../../../../api/models'

export interface NctsProfileForm {
  id: number | null
  customerId: number | null
  name: string
  grn: string
  accessCode: string
  eori: string
  companyName: string
  companyPostalCode: string
  companyStreet: string
  companyStreetNumber: string
  companyCity: string
  companyCountry: string
  guaranteeType: string
  authorityOffice: string
  authorityCountry: string
  safetyAndSecurityCountry: string
  safetyAndSecurityIdentificationNumber: string
  isPublic: boolean
}

export const blankNctsProfile: NctsProfileForm = {
  id: null,
  customerId: null,
  name: '',
  grn: '',
  accessCode: '',
  eori: '',
  companyName: '',
  companyPostalCode: '',
  companyStreet: '',
  companyStreetNumber: '',
  companyCity: '',
  companyCountry: '',
  guaranteeType: 'GUARANTEE_TYPE_0',
  authorityOffice: '',
  authorityCountry: '',
  safetyAndSecurityIdentificationNumber: '',
  safetyAndSecurityCountry: '',
  isPublic: false,
}
const baseApiUrl = getBaseApiUrl('/v1/ncts-profile')

function useNctsProfile(nctsProfileId: number | null) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const onSuccessInvalidateCache = () => {
    queryClient.invalidateQueries({ queryKey: ['/v1/ncts-profile', nctsProfileId] })
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile/customer'] })
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile/public'] })
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile/option'], exact: false })
  }

  const postNctsProfile = async (data: NctsProfileRequest) => {
    const created = (await axios.post<NctsProfileResponse>(`${baseApiUrl}`, data)).data
    onSuccessInvalidateCache()
    return created
  }

  const putNctsProfile = async (request: MutateRequest<NctsProfileRequest>) => {
    const updated = (await axios.put<NctsProfileResponse>(`${baseApiUrl}/${request.id}`, request.data)).data
    onSuccessInvalidateCache()
    return updated
  }

  const deleteNctsProfile = async (id: number) => {
    queryClient.removeQueries({ queryKey: ['/v1/ncts-profile', id] })
    await axios.delete<void>(`${baseApiUrl}/${id}`)
    queryClient.removeQueries({ queryKey: ['/v1/ncts-profile', nctsProfileId] })
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile/public'] })
    queryClient.invalidateQueries({ queryKey: ['/v1/customer-profile/customer'] })
  }

  const getNctsProfile = useQuery({
    queryKey: ['/v1/ncts-profile', nctsProfileId],
    queryFn: async ({ signal }) => (
      await axios.get<NctsProfileResponse>(`${baseApiUrl}/${nctsProfileId}`, { signal })
        .then<NctsProfileResponse>(handleResponseData)),
    enabled: !!nctsProfileId && deleteId !== nctsProfileId,
    staleTime: Infinity, // never stale
  })

  const validateProfileFields = (nctsProfile: NctsProfileForm) => {
    if (nctsProfile == null
      || isNullOrBlank(nctsProfile.name)
      || isNullOrBlank(nctsProfile.name)
      || isNullOrBlank(nctsProfile.grn)
      || isNullOrBlank(nctsProfile.accessCode)
      || isNullOrBlank(nctsProfile.eori)
      || isNullOrBlank(nctsProfile.companyPostalCode)
      || isNullOrBlank(nctsProfile.companyName)
      || isNullOrBlank(nctsProfile.companyStreet)
      || isNullOrBlank(nctsProfile.companyStreetNumber)
      || isNullOrBlank(nctsProfile.companyCountry)
      || isNullOrBlank(nctsProfile.companyCity)
    ) {
      return true
    }

    return false
  }

  const saveNctsProfile = (nctsProfile: NctsProfileForm) => {
    if (nctsProfile == null) {
      return Promise.reject(Error('Unable to save without form'))
    }
    if (validateProfileFields(nctsProfile)) {
      toast.warn(t('messages.missingRequiredFields'))
      return Promise.reject(Error('Invalid form'))
    }

    const request: NctsProfileRequest = {
      accessCode: nctsProfile.accessCode,
      authorityCountry: nctsProfile.authorityCountry,
      authorityOffice: nctsProfile.authorityOffice,
      companyCity: nctsProfile.companyCity,
      companyCountry: nctsProfile.companyCountry,
      companyName: nctsProfile.companyName,
      companyPostalCode: nctsProfile.companyPostalCode,
      companyStreet: nctsProfile.companyStreet,
      companyStreetNumber: nctsProfile.companyStreetNumber,
      safetyAndSecurityIdentificationNumber: nctsProfile.safetyAndSecurityIdentificationNumber,
      safetyAndSecurityCountry: nctsProfile.safetyAndSecurityCountry,
      eori: nctsProfile.eori,
      grn: nctsProfile.grn,
      guaranteeType: nctsProfile.guaranteeType,
      isPublic: nctsProfile.isPublic ?? false,
      name: nctsProfile.name,
    }

    if (nctsProfile?.id === null) {
      return postNctsProfile(request)
    }
    return putNctsProfile({
      id: nctsProfile.id,
      data: request,
    })
  }
  const deleteProfile = (id: number | null) => {
    if (id != null) {
      setDeleteId(id)
      return deleteNctsProfile(id).finally(() => {
        setDeleteId(null)
      })
    }
    return Promise.reject(Error('Unable to delete without id'))
  }

  return {
    getNctsProfile,
    saveNctsProfile,
    deleteProfile,
  }
}

export default useNctsProfile
